import React, { SyntheticEvent, useState, useEffect } from "react";
import {
  DropdownList,
  LoadingIndicator,
  OptionType,
  ToggleSwitchInput
} from "elements";
import { useField, useFormikContext } from "formik";
import { useTranslation } from "utilities/i18n";
import {
  FormCheckbox,
  FormInput,
  FormSelect,
  SettingsCategory
} from "routes/Manage/Inventory/Settings-Shared/DeviceSettingsComponents";
import {
  generateTimeOptions,
  LatestSoftwareVersion,
  SoftwareVersionField
} from "./ProvisionedUpdateSoftware";
import { getDeviceModel } from "./ProvisionedDeviceSettingsRouter";
import {
  AvailableOs,
  DeviceSettingsQuery,
  Enum_UpdateServerType,
  SoftwareUpdateStatus,
  SwUpdateSource,
  UpdateImageFragment,
  useEpUpdateImagesQuery,
  useOsStateQuery,
  useUpdateDeviceOsMutation
} from "generated/graphql";
import { trackAction } from "monitoring";
import { Guard } from "TsRoleGuards";
import { needs, useGuard } from "rbac";

type UpdateImage = UpdateImageFragment | null;

interface UpdateSettingsProps {
  id: string;
  model: string;
  updating: boolean;
  setUpdating: (updating: boolean) => void;
}

export const parseUpdateSource = (url: string) => {
  let updateUrl: URL;
  try {
    updateUrl = new URL(url);
  } catch {
    return SwUpdateSource.CustomUrl;
  }
  const urlRegex = /swupdate.(dev|stage|prod).lens.poly.com/i;
  const polyURL = updateUrl.hostname.match(urlRegex);

  return polyURL ? SwUpdateSource.StandardUrl : SwUpdateSource.CustomUrl;
};

const UpdateSettings: React.FC<UpdateSettingsProps> = ({
  id,
  model,
  updating,
  setUpdating
}) => {
  const { t } = useTranslation();

  const { values, initialValues } = useFormikContext<
    DeviceSettingsQuery["deviceSettings"]["settings"]
  >();

  const [, updateDeviceOs] = useUpdateDeviceOsMutation();
  const [{ value: currentServerAddress }] = useField<string>(
    "upgrade.auto.server.address"
  );

  const { fetching, data: updateData } = useOsStateQuery({
    variables: {
      filter: {
        AND: [
          {
            field: "id",
            eq: id
          }
        ]
      }
    },
    requestPolicy: "cache-and-network"
  });

  let updates = [] as Array<AvailableOs> | null | undefined;
  let currentVersion = "";
  let status = {} as SoftwareUpdateStatus | null | undefined;

  if (!fetching && updateData?.devices?.length) {
    const device = updateData.devices[0];
    updates = device.availableOSs;
    status = device.currentOSState?.updateStatus;
    currentVersion = `${device.softwareVersion}-${device.softwareBuild}`;
  }

  const handleUpdateSoftware = async () => {
    trackAction("UpdateSoftware");
    setUpdating(true);
    const result = await updateDeviceOs(
      {
        deviceId: id
      },
      { i18nKey: "DEVICE_OS_UPDATE_ERROR", dismiss: "USER_ONLY" }
    );
    if (result.error) {
      setUpdating(false);
    }
  };

  const timeOptions = generateTimeOptions();

  const saveBeforeUpdate =
    currentServerAddress !== initialValues?.upgrade?.auto?.server?.address;

  const UpdateStatusIndicator: React.FC = () => {
    let updateStatus = "";
    let error = false;
    if (fetching) {
    } else if (updating) {
      if (updates && status) {
        updateStatus = `${t("UPDATING_TO")} ${updates[0]?.version
          ?.displayName ?? ""}`;
      } else if (status === null) {
        updateStatus = t("ERROR_NO_UPDATE_STATUS");
        error = true;
      } else {
        updateStatus = t("UPDATING");
      }
    }

    return (
      <div className="form-group">
        <span
          className={`col-4 text-right available-update block ${
            error ? "error" : ""
          }`}
        >
          {updateStatus}
        </span>
      </div>
    );
  };

  const userCanEdit = useGuard(needs("update:device"));

  const deviceModel = getDeviceModel(model);

  const disableButton =
    updating || fetching || saveBeforeUpdate || !updates?.length;

  const toggleOn =
    values?.upgrade?.auto?.server?.type ===
    Enum_UpdateServerType.PolycomSupportServer;

  return (
    <>
      <SettingsCategory title={t("SOFTWARE_UPDATE")}>
        <Guard requirement="update:device">
          <div className="update-buttons" data-testid="sw-update-buttons">
            {!toggleOn && (
              <button
                type="button"
                className="btn btn-primary"
                data-testid="update-button"
                disabled={disableButton}
                onClick={handleUpdateSoftware}
              >
                {updating ? <span>{t("UPDATING")}</span> : t("UPDATE_NOW")}
              </button>
            )}
          </div>
        </Guard>
        <div className="details grid form update">
          <SoftwareVersionField
            softwareVersion={fetching ? t("LOADING") : currentVersion}
            label={"CURRENT_VERSION"}
          />
          <UpdateStatusIndicator />
          <AutoUpdateToggle disabled={!userCanEdit} />
          {toggleOn && <LatestSoftwareVersion model={deviceModel} />}
          <div
            className={`show-hide ${!toggleOn ? "show" : "hide"}`}
            data-testid="updateVersionControls"
          >
            <UpdateSource disabled={!userCanEdit} model={model} />
          </div>
          <div
            className={`show-hide ${toggleOn ? "show" : "hide"}`}
            data-testid="upgrade-schedule-form"
          >
            <FormCheckbox
              label={t("SCHEDULE_UPDATE_CHECKS")}
              name="upgrade.auto.timeFrame.enable"
              disabled={!userCanEdit}
            />
          </div>
          <div
            className={`show-hide ${
              values?.upgrade?.auto?.timeFrame?.enable && toggleOn
                ? "show"
                : "hide"
            }`}
            data-testid="upgrade-time-form"
          >
            <FormSelect
              label={t("FROM")}
              name="upgrade.auto.timeFrame.startTime"
              options={timeOptions}
              disabled={!userCanEdit}
            />
            <FormSelect
              label={t("TO")}
              name="upgrade.auto.timeFrame.stopTime"
              options={timeOptions}
              disabled={!userCanEdit}
            />
          </div>
        </div>
      </SettingsCategory>
    </>
  );
};

const AutoUpdateToggle: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<
    DeviceSettingsQuery["deviceSettings"]["settings"]
  >();

  const initialToggle =
    values?.upgrade?.auto?.server?.type ===
    Enum_UpdateServerType.PolycomSupportServer;

  // if polycom server has been chosen through the web ui, set auto.enable to true,
  // overriding any web ui choice
  useEffect(() => {
    if (initialToggle) {
      setFieldValue("upgrade.auto.enable", true);
    }
  }, [initialToggle, setFieldValue]);

  const onChange = (event: SyntheticEvent) => {
    const target = event.target as HTMLInputElement;
    const toggleOn = target.checked;

    const serverType = toggleOn
      ? Enum_UpdateServerType.PolycomSupportServer
      : Enum_UpdateServerType.UserDefinedServer;

    setFieldValue("upgrade.auto.server.type", serverType);
    setFieldValue("upgrade.auto.enable", toggleOn);
  };

  return (
    <div className="form-group">
      <label className="col-4 text-right" htmlFor={"latestUpdate"}>
        {t("AUTO_UPDATE_LATEST")}:
      </label>
      <div className="col-4">
        <ToggleSwitchInput
          checked={initialToggle}
          onChange={onChange}
          disabled={disabled}
          name="latestUpdate"
        />
      </div>
    </div>
  );
};

const UpdateSource: React.FC<{ disabled: boolean; model: string }> = ({
  disabled,
  model
}) => {
  const { t } = useTranslation();

  const [{ value }, , { setValue }] = useField<string>(
    "upgrade.auto.server.address"
  );

  const initialUpdateSource = parseUpdateSource(value);

  const [updateSource, setUpdateSource] = useState<SwUpdateSource | string>(
    initialUpdateSource
  );

  // state for custom URL field and version dropdown
  const [customChoice, setCustomChoice] = useState("");
  const [dropdownChoice, setDropdownChoice] = useState("");

  // update the server.address field when the user switches update sources
  const changeUpdateSource = (source: string) => {
    setUpdateSource(source);
    const choice =
      source === SwUpdateSource.CustomUrl ? customChoice : dropdownChoice;
    if (choice && value !== choice) setValue(choice);
  };

  const updateDropdownChoice = (choice: string) => {
    setDropdownChoice(choice);
    setValue(choice);
  };

  const updateSourceOptions = [
    {
      value: SwUpdateSource.StandardUrl,
      text: t("POLY_LENS")
    },
    {
      value: SwUpdateSource.CustomUrl,
      text: t("CUSTOM_SERVER")
    }
  ];

  return (
    <>
      <div className={"form-group"}>
        <label className="col-4 text-right">{t("DOWNLOAD_SOURCE")}:</label>
        <div className="col-4">
          <DropdownList
            name="updateSource"
            value={updateSource}
            options={updateSourceOptions}
            disabled={disabled}
            onChange={changeUpdateSource}
          />
        </div>
      </div>
      <div
        className={`show-hide ${
          updateSource === SwUpdateSource.CustomUrl ? "show" : "hide"
        }`}
        data-testid="updateServerAddress"
      >
        <FormInput
          label={t("UPDATE_SERVER_ADDRESS")}
          name="customChoice"
          value={customChoice}
          onChange={e => setCustomChoice(e.target.value)}
          disabled={disabled}
          onBlur={e => setValue(e.target.value)}
        />
      </div>
      <div
        className={`show-hide ${
          updateSource === SwUpdateSource.StandardUrl ? "show" : "hide"
        }`}
        data-testid="imagesDropdown"
      >
        <ImagesDropdown
          deviceModel={model}
          disabled={disabled}
          dropdownChoice={dropdownChoice}
          label={t("SELECT_VERSION")}
          updateDropdownChoice={updateDropdownChoice}
        />
      </div>
    </>
  );
};

const ImagesDropdown: React.FC<{
  deviceModel: string;
  disabled: boolean;
  dropdownChoice: string;
  label: string;
  updateDropdownChoice: (value: string) => void;
}> = ({
  deviceModel,
  disabled,
  dropdownChoice,
  label,
  updateDropdownChoice
}) => {
  const { data: imagesData, fetching: imagesFetching } = useEpUpdateImagesQuery(
    {
      variables: { deviceModel, isGlobal: true }
    }
  );

  let images: Array<UpdateImage> = [];

  if (!imagesFetching && imagesData?.getEpUpdateImages) {
    images = imagesData!.getEpUpdateImages;
  } else if (imagesFetching) {
    return <LoadingIndicator />;
  } else {
    return null;
  }

  const versionOptions: Array<OptionType> = images.map(image => ({
    text: image?.imageVersion || "",
    value: image?.directory || ""
  }));

  return (
    <div className={"form-group"}>
      <label className="col-4 text-right">{label}:</label>
      <div className="col-4">
        <DropdownList
          name="versionChoice"
          value={dropdownChoice}
          options={versionOptions}
          disabled={disabled}
          onChange={updateDropdownChoice}
        />
      </div>
    </div>
  );
};

export default UpdateSettings;
